<script lang="ts" setup>
import { computed, onMounted, ref, useId, useSlots, useTemplateRef } from 'vue'
import loading from '@/components/common/loading.vue'

import { bgColors,size as sizeConst } from '@/services/application/constantes'

const props = withDefaults(defineProps<{
  containerClass?: string
  inputContainerClass?: string
  autocomplete?: string
  inputType?: 'tel' | 'search' | 'text' | 'email' | 'number' | 'password' | 'file' | 'url' | 'date' | 'month' | 'week' | 'time' | 'datetime' | 'datetime-local' | 'range' | 'color' | 'month'
  label?: string
  labelFixed?: boolean
  autofocus?: boolean
  placeholder?: string
  disabled?: boolean
  errorMessage?: string
  shadow?: string
  isLoading?: boolean
  size?: keyof typeof sizeConst
  required?: boolean
  id?: string
  bg?: keyof typeof bgColors
  minMax?: { min: number, max: number } // for number input
  precision?: number // for number input
}>(), {
  labelFixed: false,
  autofocus: false,
  inputType: 'text',
  autocomplete: '',
  disabled: false,
  placeholder: '',
  shadow: 'shadow',
  isLoading: false,
  size: 'small',
  required: false,
  precision: undefined,
})
const emit = defineEmits(['leaveInput', 'contentChanged'])
const slots = useSlots()

const generatedId = useId()
const resolvedId = computed(() => props.id || generatedId)
const browserLang = typeof navigator !== 'undefined' ? navigator.language : undefined


const textValue = defineModel<string | number>()

const inputElement = useTemplateRef<HTMLInputElement>('inputElement')

const focusInput = () => {
  inputElement.value.focus()
}

// The native attribute only fires for the first autofocus of the document: an input mounted
// later (modal, picker, in-app navigation) has to be focused by hand.
onMounted(() => {
  if (props.autofocus)
    inputElement.value?.focus({ preventScroll: true })
})

const onBlur = () => {
  emit('leaveInput')
}

const inputValue = computed({
  get() {
    if (props.inputType === 'number' && typeof props.precision === 'number') {
      const numeric = Number(textValue.value)
      if (Number.isFinite(numeric))
        // Round to precision without forcing trailing zeros (6 instead of 6.00)
        return String(Number(numeric.toFixed(props.precision)))
    }
    return textValue.value ?? ''
  },
  set(value) {
    if (props.inputType === 'number' && typeof props.precision === 'number') {
      const numeric = Number(String(value ?? '').replace(',', '.'))
      textValue.value = Number.isFinite(numeric)
        ? Number(numeric.toFixed(props.precision))
        : value as any
    }
    else {
      textValue.value = value as any
    }
  },
})


const topDistance = computed(() => {
  if (props.size === 'dense')
    return '-1.2rem'
  if (props.size === 'small')
    return '-1.4rem'
  if (props.size === 'medium')
    return '-1.5rem'
  if (props.size === 'large')
    return '-1.7rem'  

  return '0.2rem'
})

</script>

<script lang="ts">
// so that we can pass attributes to the input element instead of the wrapper div
export default {
  inheritAttrs: false,
}
</script>

<template>
  <div class="flex" :class="containerClass"> 
    <slot name="prepend" />
    <div class="flex flex-col grow relative">

      <div
        class="grow group relative group border-base border bg-base rounded-lg focus focus-within:border-primary-600 focus-within:outline-hidden px-3 active:outline-hidden active:border-primary-600 flex flex-wrap"
        :class="[inputContainerClass, { 'mt-5': label }, shadow , sizeConst[size]]"
        @click="focusInput"
      >
        <span class="absolute left-2 top-1/2 -translate-y-1/2 subtitle">
          <slot name="prepend-icon" />
        </span>
        <input
          ref="inputElement" v-bind="$attrs" v-model="inputValue"
          :id="resolvedId"
          :name="$attrs.name || resolvedId"
          :class="[{ filled: labelFixed || placeholder, 'cursor-not-allowed': disabled }, { 'ml-4': !!slots['prepend-icon'] }]"
          class="input appearance-none w-full outline-hidden bg-transparent" :placeholder="placeholder"
          :autofocus="autofocus" :autocomplete="autocomplete" :disabled="disabled" :type="inputType"
          :lang="inputType === 'number' ? browserLang : undefined"
          :min="minMax?.min" :max="minMax?.max"
          :required="required || undefined"
          :aria-required="required || undefined"
          :aria-invalid="errorMessage ? true : undefined"
          :aria-describedby="errorMessage ? `${resolvedId}-error` : undefined"
          :aria-busy="isLoading || undefined"
          @blur="onBlur" @input="$emit('contentChanged')"
        >

        <label
          v-if="label" 
          :for="resolvedId"
          class="label absolute leading-tighter text-gray-600 dark:text-gray-400 text-base cursor-text line-clamp-1"
          :class="[ sizeConst[size] ]"
        >
          {{ label }} 
          <span v-if="required" class="text-danger-400 text-sm" aria-hidden="true">*</span>
        </label>
      </div>
      <transition name="shake">
        <p
          v-show="errorMessage"
          :id="`${resolvedId}-error`"
          class="text-red-400 text-sm"
          role="alert"
        >
          {{ errorMessage }}
        </p>
      </transition>
      <span v-if="isLoading" class="absolute right-2 bottom-2 -translate-x-1/2" aria-hidden="true">
        <loading />
      </span>
    </div>
    <slot name="append" />
  </div>
</template>

<style scoped>
.input {
  transition: border 0.2s ease-in-out;
}
.input:not(:placeholder-shown) ~ .label,
.input:focus ~ .label,
.input:active ~ .label,
.input.filled ~ .label {
  transition: all 0.2s ease;
  padding: 0;
}

.label {
  transition: all 0.2s ease-out;
  left: 0;
  top: v-bind(topDistance);
  padding: 0;
}
</style>
